<template>
  <component
    :is="element"
    class="media-section critical-component-clear"
    :style="`background-color: ${sectionSettings.backgroundColor};
    --padding-bottom-mobile: ${sectionSettings.paddingBottomMobile
    ? sectionSettings.paddingBottomMobile : 0}px;
    --padding-top-mobile: ${sectionSettings.paddingTopMobile
    ? sectionSettings.paddingTopMobile : 0}px;
    --padding-bottom-desktop: ${sectionSettings.paddingBottomDesktop ?
    sectionSettings.paddingBottomDesktop : 0}px;
    --padding-top-desktop: ${sectionSettings.paddingTopDesktop
    ? sectionSettings.paddingTopDesktop : 0}px;
    `"
    :href="sectionUrl"
    :class="[
      {
        [$class('hidden')]: isMobile ?
          sectionSettings.hideMobile : sectionSettings.hideDesktop
      },
      sectionSettings.isFirst && 'media-section--is-first',
      desktopMediaSettings.desktopHasPortraitMedia && 'media-section--desktop-has-portrait-media',
      mobileMediaSettings.mobileHasPortraitMedia && 'media-section--mobile-has-portrait-media',
      sectionSettings.contentColor && `section-content--${sectionSettings.contentColor}`,
      sectionSettings.layout && `media-section--${sectionSettings.layout}`,
    ]"
  >
    <media-content
      :component-settings="{
        contentColor: sectionSettings.contentColor,
        mediaSection: sectionSettings.mediaSection,
        caption: sectionSettings.caption,
        layout: sectionSettings?.layout || '',
        mobileHeight: sectionSettings?.mobileHeight || '',
        hasContent: true,
        hasCaption: false,
        hasIndexLayout: true,
        hasStickyContent: sectionSettings.hasStickyContent,
        autoplay: sectionSettings.autoplay,
        loop: sectionSettings.loop,
        muted: sectionSettings.muted,
        titleText: sectionSettings.titleText,
        titleHorizontalPosition: sectionSettings.titleHorizontalPosition,
        titleVerticalPosition: sectionSettings.titleVerticalPosition,
        titleDesktopSize: sectionSettings.titleDesktopSize,
        titleMobileSize: sectionSettings.titleMobileSize,
        titleAndButtonOneRowLayout: sectionSettings.titleAndButtonOneRowLayout,
        titleMarginBottom: sectionSettings.titleMarginBottom,
        horizontalPosition: sectionSettings?.horizontalPosition,
        verticalPosition: sectionSettings?.verticalPosition,
        buttonText: sectionSettings.buttonText,
        buttonURL: sectionSettings.buttonURL,
        kickerLine1: sectionSettings.kickerLine1,
        kickerLine2: sectionSettings.kickerLine2,
        kickerPosition: sectionSettings.kickerPosition,
        bodyText: sectionSettings.bodyText,
        linksPosition: sectionSettings.linksPosition,
        layout: sectionSettings.layout,
        width: sectionSettings.width,
        hasControls: sectionSettings.hasControls,
        contentType: sectionSettings.contentType,
        revealEffect: sectionSettings.revealEffect,
      }"
      :content-class="contentClass"
      :content-placement="contentPlacement"
      :show-media="showMedia"
      :show-content="showContent"
      :mobile-media-settings="mobileMediaSettings"
      :desktop-media-settings="desktopMediaSettings"
      :link-settings="linkSettings"
      :tab-settings="tabSettings"
      parent="media-section"
      @carousel-image-loaded="removePlaceholders"
    />
  </component>
</template>

<script>

/**
 * Vue: Full bleed banner (media-section)
 * -----------------------------------------------------------------------------
 * Full Bleed Banner Section.
 *
 * @param {String} contentPlacement - String indicating if the component is
 * occupying half or full width.
 *
 */
import { mapState } from 'vuex'
import MediaContent from '~async/media-content/media-content.vue'

export default {
  name: 'MediaSection',
  components: {
    MediaContent,
  },
  props: {
    contentClass: {
      type: String,
      default: '',
    },

    contentPlacement: {
      type: String,
      default: 'full',
    },

    showMedia: {
      type: Boolean,
      default: true,
    },

    showContent: {
      type: Boolean,
      default: true,
    },

    sectionSettings: {
      type: Object,
      default: () => ({
        titleText: '',
        titleDesktopSize: '',
        titleMobileSize: '',
        titleHorizontalPosition: '',
        titleVerticalPosition: '',
        bodyText: '',
        bodyMobileSize: '',
        bodyDesktopSize: '',
        kickerLine1: '',
        kickerLine2: '',
        kickerPosition: '',
        linksPosition: '',
        stickyContent: false,
        buttonText: '',
        buttonURL: '',
        buttonSticky: true,
        hideDesktop: '',
        hideMobile: '',
        backgroundColor: '',
        contentColor: '',
        contentType: '',
        revealEffect: false,
        layout: '',
        mediaSection: '',
        titleAndButtonOneRowLayout: false,
        width: '',
      }),
    },

    linkSettings: {
      type: Array,
      default: () => [
        {
          title: '',
          text: '',
          url: '',
        },
      ],
    },

    tabSettings: {
      type: [Boolean, Object],
      default: () => false,
    },

    mobileMediaSettings: {
      type: Object,
      default: () => ({
        mobileDefaultHeight: false,
        mobileVideoFile: '',
        mobileVimeoId: '',
        mobileImage: '',
        mobileHasPortraitMedia: false,
      }),
    },

    desktopMediaSettings: {
      type: Object,
      default: () => ({
        desktopDefaultHeight: false,
        desktopVideoFile: '',
        desktopVimeoId: '',
        desktopImage: '',
        desktopHasPortraitMedia: false,
      }),
    },
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute element type.
     * @returns {String}
     */
    element() {
      return this.sectionSettings.buttonURL &&
        (!this.linkSettings[0]?.title?.length &&
          !this.linkSettings[0]?.url.length && !this.tabSettings)
        ? 'a'
        : 'div'
    },

    /**
     * Compute element url.
     * @returns {String}
     */
    sectionUrl() {
      return this.sectionSettings.buttonURL ? this.sectionSettings.buttonURL : null
    },
  },

  methods: {

    /**
     * Removes the liquid placeholders AFTER the vue media has loaded
     * and fully rendered.
     */
    removePlaceholders() {
      this.$nextTick().then(() => {
        const parent = this.$el.closest('section')
        const placeholder = parent.querySelector('.liquid-removed')
        if (!placeholder) {
          return
        }

        placeholder.remove()
      })
    },
  },
}
</script>

<style lang="scss">
@import './media-section';
</style>
